/**
 * `ConfirmDeleteDialog` — a shared dark + orange replacement for
 * `window.confirm(...)` on destructive actions. Used by the Ledger, entry
 * edit page, Jobs, and Personal Tax rental properties so every delete
 * confirm looks and behaves the same. Destructive action is red (never the
 * default iOS blue), cancel is a quiet ghost button on the dark surface.
 *
 * Usage (controlled):
 *   const [open, setOpen] = useState(false);
 *   const [pending, setPending] = useState(null);
 *   const askDelete = (row) => setPending(row);
 *   const confirm = async () => { await api.delete(...); setPending(null); };
 *   <ConfirmDeleteDialog
 *     open={!!pending}
 *     title="Delete this entry?"
 *     description="This can't be undone."
 *     onConfirm={confirm}
 *     onCancel={() => setPending(null)}
 *   />
 */
import { Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ConfirmDeleteDialog({
  open,
  title = "Delete this entry?",
  description = "This can't be undone.",
  confirmLabel = "Delete",
  onConfirm,
  onCancel,
  testId = "confirm-delete",
}) {
  const [busy, setBusy] = useState(false);
  const handleConfirm = async () => {
    if (!onConfirm) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog
      open={open}
      onOpenChange={(v) => {
        if (!v && !busy) onCancel?.();
      }}
    >
      <AlertDialogContent
        data-testid={`${testId}-dialog`}
        className="bg-[#1F1F1F] border border-white/10 text-white rounded-2xl"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-white/60">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel
            data-testid={`${testId}-cancel`}
            disabled={busy}
            className="bi-ghost-btn text-white hover:brightness-110 border-white/10"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testId}-confirm`}
            disabled={busy}
            onClick={(e) => { e.preventDefault(); handleConfirm(); }}
            className="bg-red-500/90 hover:bg-red-500 text-white font-heading font-bold inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
